/**
 * Image reveal — zoom-in on load.
 *
 * Adds `is-loaded` to `img.image-zoom-in`, which triggers the zoom transition
 * defined in base.css. Two variants exist (see the CSS for the full rationale):
 *
 *  - lazy images scale down from an oversized crop and fade in;
 *  - eager images (`image-zoom-in--eager`, above the fold) scale only, never
 *    touching opacity, so an LCP candidate paints on its first frame.
 *
 * Rules this file guarantees:
 *  - It never touches `src` or `srcset`. Loading is entirely native.
 *  - The animation plays on every page load, including for images served from
 *    the HTTP cache: a cached image is already decoded, so the zoom starts
 *    immediately instead of being skipped.
 *  - It never leaves an image invisible. Load failures, bfcache restores and
 *    stalled on-screen requests all resolve to the plain visible state.
 */ 

/**
 * Modernized Image Reveal — Zoom-in on load.
 */
(() => {
  'use strict';

  const SELECTOR = 'img.image-zoom-in';
  const STALL_DELAY = 4000;
  const TRANSITION_DURATION = 950;

  // Track active timers to prevent memory leaks and redundant execution
  const stallTimers = new WeakMap();

  function clearStallTimer(img) {
    if (stallTimers.has(img)) {
      clearTimeout(stallTimers.get(img));
      stallTimers.delete(img);
    }
  }

  function settle(img) {
    setTimeout(() => {
      img.classList.add('is-revealed');
    }, TRANSITION_DURATION);
  }

  function animate(img) {
    if (img.dataset.revealed === 'true') return;
    img.dataset.revealed = 'true';
    clearStallTimer(img);

    // Trigger transition on the next immediate paint
    requestAnimationFrame(() => {
      img.classList.add('is-loaded');
      settle(img);
    });
  }

  function settleImmediately(img) {
    if (img.dataset.revealed === 'true') return;
    img.dataset.revealed = 'true';
    clearStallTimer(img);

    img.classList.add('is-instant', 'is-loaded', 'is-revealed');
  }

  // IntersectionObserver for stall fallback
  let stallWatcher = null;
  if ('IntersectionObserver' in window) {
    stallWatcher = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const img = entry.target;
          stallWatcher.unobserve(img);

          if (img.dataset.revealed !== 'true') {
            const timer = setTimeout(() => settleImmediately(img), STALL_DELAY);
            stallTimers.set(img, timer);
          }
        });
      },
      { rootMargin: '200px' }
    );
  }

  function track(img) {
    if (img.dataset.revealTracked === 'true') return;
    img.dataset.revealTracked = 'true';

    // Fast-path: Image is already loaded/cached
    if (img.complete) {
      if (img.naturalWidth === 0) {
        settleImmediately(img);
      } else {
        animate(img);
      }
      return;
    }

    const onLoad = () => {
      if (stallWatcher) stallWatcher.unobserve(img);
      animate(img);
    };

    const onError = () => {
      if (stallWatcher) stallWatcher.unobserve(img);
      settleImmediately(img);
    };

    img.addEventListener('load', onLoad, { once: true });
    img.addEventListener('error', onError, { once: true });

    if (stallWatcher) stallWatcher.observe(img);
  }

  function scan(root = document) {
    const images = root.querySelectorAll(SELECTOR);
    for (let i = 0; i < images.length; i++) {
      track(images[i]);
    }
  }

  function settleAll() {
    document.querySelectorAll(SELECTOR).forEach(settleImmediately);
  }

  // Initial Scan
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else {
    scan();
  }

  // Dynamic Content Observer
  if ('MutationObserver' in window) {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches(SELECTOR)) {
            track(node);
          } else if (node.querySelectorAll) {
            scan(node);
          }
        }
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  // Safety net: by the load event nothing should still be sitting at opacity 0.
  // Any image that finished decoding before its listener was attached (a race
  // this script cannot observe) is settled without an animation rather than
  // left invisible until the 4s stall timer.
  window.addEventListener(
    'load',
    () => {
      document.querySelectorAll(SELECTOR).forEach((img) => {
        if (img.dataset.revealed === 'true') return;
        if (!img.complete) return;
        settleImmediately(img);
      });
    },
    { once: true }
  );

  // Handle back/forward cache (bfcache)
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) settleAll();
  });
})();